import { Component, OnDestroy, OnInit, ViewEncapsulation, HostListener, HostBinding } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CoreConfigService } from '@core/services/config.service';

@Component({
    selector: 'horizontal-layout',
    templateUrl: './horizontal-layout.component.html',
    styleUrls: ['./horizontal-layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HorizontalLayoutComponent implements OnInit, OnDestroy {
  coreConfig: any;

  // Navbar auto-hide state
  navbarPinned: boolean = true;
  private headerHoverShowing: boolean = false;
  private headerScrolled: boolean = false;

  @HostBinding('class.header-autohide-hidden')
  headerHidden: boolean = false;

  /** Enables CSS transitions only after initial render — prevents animation flash on login */
  @HostBinding('class.header-autohide-ready')
  navbarReady: boolean = false;

  private readonly SCROLL_THRESHOLD = 80;
  private readonly NAVBAR_HEIGHT = 62;
  private readonly MENU_HEIGHT = 50;
  private readonly HOVER_TRIGGER_PX = 20; // trigger zone when header is hidden

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   */
  constructor(private _coreConfigService: CoreConfigService) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (!this.navbarPinned && this.coreConfig?.layout?.type === 'horizontal') {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      if (scrollTop > this.SCROLL_THRESHOLD) {
        if (!this.headerScrolled) {
          this.headerScrolled = true;
          this.headerHoverShowing = false;
          this.headerHidden = true;
        }
      } else {
        if (this.headerScrolled) {
          this.headerScrolled = false;
          this.headerHidden = false;
        }
      }
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.navbarPinned && this.headerScrolled) {
      // Show when mouse is near the top, hide when it moves away
      const totalHeaderHeight = this.NAVBAR_HEIGHT + this.MENU_HEIGHT; // navbar + menu height
      const triggerZone = this.headerHoverShowing ? totalHeaderHeight : this.HOVER_TRIGGER_PX;
      const shouldShow = event.clientY <= triggerZone;
      if (shouldShow !== this.headerHoverShowing) {
        this.headerHoverShowing = shouldShow;
        this.headerHidden = !shouldShow;
      }
    }
  }

  onPinToggle(): void {
    this.navbarPinned = !this.navbarPinned;
    localStorage.setItem('navbarPinned', String(this.navbarPinned));
    if (this.navbarPinned) {
      this.headerScrolled = false;
      this.headerHoverShowing = false;
      this.headerHidden = false;
    } else {
      // Re-evaluate scroll state immediately
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      if (scrollTop > this.SCROLL_THRESHOLD) {
        this.headerScrolled = true;
        this.headerHidden = true;
      }
    }
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Load navbar pin state from localStorage (default: pinned = true)
    this.navbarPinned = localStorage.getItem('navbarPinned') !== 'false';

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
    });

    // Enable CSS transitions after the first render cycle to avoid animation flash on login
    setTimeout(() => { this.navbarReady = true; }, 300);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
